// PreviewBlocklyWorkspace - プレビュー画面の Blockly ワークスペース層
// 旧ビルダープレビュー blockly/index.tsx から復元。左の細いフライアウト列に
// enabledCommands のブロックを表示し、上部に「コマンド」ヘッダー（CommandSvg）を出す。

import { useSelector } from "react-redux";
import { useMemo, useState, useEffect } from "react";
import * as Blockly from "blockly";
import { RootState } from "app/store";
import Constants from "common/constant";
import { useScale } from "common/utils";
import { CommandSvg } from "features/courses/assets";
import { useBlocklyInit } from "./initial";
import { buildMaxInstances } from "./Toolbox";
import { PreviewBlockLimits } from "../components/PreviewBlockLimits";

export const PreviewBlocklyWorkspace = ({
  gameStart,
  topOffset,
  commandPanelHeight,
  workspace,
  setWorkspace,
  flyoutWidth,
  flyoutHeight,
  setEvent,
}: {
  gameStart: boolean;
  topOffset: number;
  commandPanelHeight: number;
  workspace: Blockly.WorkspaceSvg;
  setWorkspace: (workspace: Blockly.WorkspaceSvg) => void;
  flyoutWidth: number;
  flyoutHeight: number;
  setEvent: (code: string) => void;
}) => {
  const { scale } = useScale();
  const BLOCK_LIMITS_WIDTH = 108;
  const [drag, setDrag] = useState(false);
  const config = useSelector((state: RootState) => state.config.userConfig);
  const enabledCommands = useSelector((state: RootState) => state.builder.enabledCommands);
  const commandLimits = useSelector((state: RootState) => state.builder.commandLimits);

  // コマンド別の使用個数上限（Blockly maxInstances 形式）と、のこり個数の表示用 state
  const maxInstances = useMemo(() => buildMaxInstances(commandLimits), [commandLimits]);
  const [remaining, setRemaining] = useState<{ [type: string]: number }>({});

  useBlocklyInit(
    scale,
    topOffset + commandPanelHeight,
    config.locale,
    enabledCommands,
    maxInstances,
    workspace,
    setWorkspace,
    setEvent,
    setRemaining,
  );

  useEffect(() => {
    if (workspace) {
      const flyout = workspace.getFlyout();
      // @ts-expect-error Blockly 7.x の型に onDragOver 等が無いため
      flyout.onDragOver = () => setDrag(true);
      // @ts-expect-error Blockly 7.x の型に onDrop が無いため
      flyout.onDrop = () => setDrag(false);
      // @ts-expect-error Blockly 7.x の型に onDragExit が無いため
      flyout.onDragExit = () => setDrag(false);
    }
  }, [workspace]);

  return (
    <div className="flex-col-view !flex-1 w-full h-full">
      <div
        className="flex-col-view !absolute overflow-hidden"
        style={{
          zIndex: gameStart ? 50 : 0,
          top: topOffset * scale,
          width: flyoutWidth + BLOCK_LIMITS_WIDTH * scale,
          height: flyoutHeight - topOffset * scale,
        }}
      >
        <div
          className="flex-col-view h-full bg-white/60 border-solid rounded-xl border-2 border-white backdrop-blur-sm"
          style={{ width: flyoutWidth + BLOCK_LIMITS_WIDTH * scale }}
        />
      </div>

      <div
        className="flex-col-center rounded-xl bg-white !absolute z-[100] opacity-70"
        style={{
          display: drag ? "flex" : "none",
          top: topOffset * scale,
          width: flyoutWidth + BLOCK_LIMITS_WIDTH * scale,
          height: flyoutHeight - topOffset * scale,
        }}
      >
        <div className="flex-col-center">
          <img alt="trash" src={`${Constants.assetHost}/assets/images/trash.png`} />
        </div>
      </div>

      <div
        className="flex-col-center !absolute pointer-events-auto"
        style={{
          top: topOffset * scale,
          width: flyoutWidth + BLOCK_LIMITS_WIDTH * scale,
          height: commandPanelHeight * scale,
        }}
      >
        <div
          className="flex-col-center"
          style={{
            width: 180,
            height: 32,
            transform: `scale(${scale})`,
            margin: `${25 * scale}px 0`,
          }}
        >
          <CommandSvg />
        </div>
      </div>

      <div className="flex-row-el items-start !flex-1 w-full">
        {/* コマンド別の「のこり N」列（個数制限のあるブロックのみ。algorithm と同じ左列配置） */}
        <PreviewBlockLimits
          enabledCommands={enabledCommands}
          limits={commandLimits}
          remaining={remaining}
          offset={topOffset + commandPanelHeight}
          width={BLOCK_LIMITS_WIDTH}
          height={flyoutHeight}
          scale={scale}
        />
        <div id="blocklyDiv" className="flex-col-el !flex-1 w-full h-full" />
      </div>
    </div>
  );
};
